/*
 * Commands store — the client half of the concept.md C.5 lifecycle.
 *
 * A "pending command" is one the user just submitted; we watch the SignalR
 * `commands` channel for its lifecycle events and drive a toast card through
 * Sent → Ack → Executed/Failed/Timeout. Once terminal, the card auto-hides
 * after a few seconds; the entry itself is removed from `pendingCommandsSignal`
 * so future views can be sure the "in flight" set really is in flight.
 *
 * There is no retry logic here — concept C.5 is explicit that timeouts are
 * fail-loud and the user re-issues consciously.
 *
 * The client-side timeout (45 s) is a safety margin around the server's 30 s.
 * If the server broadcast reaches us first (the expected path) we cancel our
 * timer.
 */

import { signal, type Signal } from '@preact/signals';
import type { CommandEvent, CommandStatus } from '../api/types';
import { onCommands, ensureHubStarted } from './hubClient';
import { onCommandEvent as onCommandWriteEvent } from './writeStore';
import { showToast, dismissToast } from '../components/Toaster';

/** In-flight (or just-finished) command we're tracking on behalf of the user.
 *  Terminal entries stay in the map briefly so a component that re-reads the
 *  history can find them before the toast auto-hides. */
export interface InFlightCommand {
  correlationId: string;
  plantId: string;
  leafId: string;
  leafSymbol: string;
  /** Display-friendly label ("Setpoint AN", "42.0 °C", etc.). We pre-format at
   *  submit time so the toast doesn't need the leaf metadata later. */
  valueLabel: string;
  submittedAt: number;
  status: CommandStatus;
  error?: string;
  terminalAt?: number;
}

export const pendingCommandsSignal: Signal<Map<string, InFlightCommand>> = signal(
  new Map<string, InFlightCommand>(),
);

/** Server times out at 30 s (SendCommandOptions.TimeoutSeconds); we give
 *  ourselves a 15 s safety margin before firing the client-side fallback. */
const CLIENT_TIMEOUT_MS = 45_000;

/** After a terminal event, how long the toast stays on screen. Also drives
 *  removal from the in-flight map. */
const TERMINAL_HIDE_MS = 6_000;

/** Per-correlation client-side timeout handles. Cancelled when a terminal
 *  server event arrives, or when the user manually dismisses. */
const clientTimeouts = new Map<string, number>();

function isTerminal(status: CommandStatus): boolean {
  return status === 'Executed' || status === 'Failed' || status === 'Timeout';
}

function toastToneFor(status: CommandStatus): 'info' | 'success' | 'warn' | 'error' {
  switch (status) {
    case 'Sent':
    case 'Ack':
      return 'info';
    case 'Executed':
      return 'success';
    case 'Failed':
      return 'error';
    case 'Timeout':
      return 'warn';
    default:
      return 'info';
  }
}

function toastTitleFor(cmd: InFlightCommand): string {
  const prefix = cmd.leafSymbol;
  switch (cmd.status) {
    case 'Sent':
      return `${prefix} — gesendet`;
    case 'Ack':
      return `${prefix} — bestätigt`;
    case 'Executed':
      return `${prefix} — ausgeführt`;
    case 'Failed':
      return `${prefix} — Fehler`;
    case 'Timeout':
      return `${prefix} — Timeout`;
    default:
      return prefix;
  }
}

function toastMessageFor(cmd: InFlightCommand): string | undefined {
  if (cmd.status === 'Failed' || cmd.status === 'Timeout') {
    return cmd.error ?? (cmd.status === 'Timeout'
      ? 'Keine Rückmeldung innerhalb der Timeout-Frist.'
      : 'Der Schreibvorgang ist fehlgeschlagen.');
  }
  return `Wert: ${cmd.valueLabel}`;
}

function refreshToast(cmd: InFlightCommand): void {
  const terminal = isTerminal(cmd.status);
  showToast({
    id: cmd.correlationId,
    correlationId: cmd.correlationId,
    tone: toastToneFor(cmd.status),
    title: toastTitleFor(cmd),
    message: toastMessageFor(cmd),
    // Pending-Toasts sind bewusst sticky (0), sonst wuerde der Sent/Ack-
    // Balken nach 5s (Default) verschwinden bevor der Regler geantwortet
    // hat. Erst der terminal-Update kriegt eine Auto-Hide-Frist.
    autoHideAfterMs: terminal ? TERMINAL_HIDE_MS : 0,
  });
}

function updateEntry(correlationId: string, updater: (prev: InFlightCommand) => InFlightCommand): void {
  const map = pendingCommandsSignal.value;
  const existing = map.get(correlationId);
  if (!existing) return;

  const next = updater(existing);
  const copy = new Map(map);
  copy.set(correlationId, next);
  pendingCommandsSignal.value = copy;

  refreshToast(next);

  if (isTerminal(next.status)) {
    const clientHandle = clientTimeouts.get(correlationId);
    if (clientHandle !== undefined) {
      window.clearTimeout(clientHandle);
      clientTimeouts.delete(correlationId);
    }

    // Drop the entry from the map after the toast has auto-hidden. Consumers
    // reading pendingCommandsSignal shortly after a terminal event still see
    // it, but a minute later it's gone.
    window.setTimeout(() => {
      const now = pendingCommandsSignal.value;
      if (!now.has(correlationId)) return;
      const trimmed = new Map(now);
      trimmed.delete(correlationId);
      pendingCommandsSignal.value = trimmed;
    }, TERMINAL_HIDE_MS + 500);
  }
}

export interface TrackCommandInput {
  correlationId: string;
  plantId: string;
  leafId: string;
  leafSymbol: string;
  valueLabel: string;
}

/** Register a just-submitted command. Called by the modal after the endpoint
 *  returns 202. Inserts a `Sent` entry and starts a client-side timeout that
 *  promotes to `Timeout` if the server doesn't get there first. */
export function trackCommand(input: TrackCommandInput): void {
  const entry: InFlightCommand = {
    correlationId: input.correlationId,
    plantId: input.plantId,
    leafId: input.leafId,
    leafSymbol: input.leafSymbol,
    valueLabel: input.valueLabel,
    submittedAt: Date.now(),
    status: 'Sent',
  };

  const map = new Map(pendingCommandsSignal.value);
  map.set(input.correlationId, entry);
  pendingCommandsSignal.value = map;

  refreshToast(entry);

  const handle = window.setTimeout(() => {
    clientTimeouts.delete(input.correlationId);
    // Only promote if we're still non-terminal.
    const current = pendingCommandsSignal.value.get(input.correlationId);
    if (!current || isTerminal(current.status)) return;
    updateEntry(input.correlationId, (prev) => ({
      ...prev,
      status: 'Timeout',
      terminalAt: Date.now(),
      error: 'Keine Rückmeldung innerhalb der Timeout-Frist (client-seitig).',
    }));
  }, CLIENT_TIMEOUT_MS);
  clientTimeouts.set(input.correlationId, handle);
}

/** Manually dismiss an in-flight command's toast + entry. Used by the "clear"
 *  affordance on the toast card. */
export function forgetCommand(correlationId: string): void {
  const handle = clientTimeouts.get(correlationId);
  if (handle !== undefined) {
    window.clearTimeout(handle);
    clientTimeouts.delete(correlationId);
  }
  dismissToast(correlationId);
  const map = pendingCommandsSignal.value;
  if (!map.has(correlationId)) return;
  const copy = new Map(map);
  copy.delete(correlationId);
  pendingCommandsSignal.value = copy;
}

function handleServerEvent(evt: CommandEvent): void {
  // The server broadcasts to `commands-{userId}`, so every event on this
  // channel is meant for us. But it may reference a command we didn't submit
  // in this browser session (another tab, page reload, etc.) — in that case
  // we simply have no entry to update. That's fine; we just ignore it.
  updateEntry(evt.c, (prev) => ({
    ...prev,
    status: evt.s,
    error: evt.e ?? prev.error,
    terminalAt: isTerminal(evt.s) ? Date.parse(evt.t) : prev.terminalAt,
  }));
  // Zusätzlicher Fan-out an den writeStore: der trackt Stage 2 (Ack →
  // Readback) und dekoriert die Leaf-Zeile im DP-Viewer entsprechend.
  // `v` traegt den Immediate-Readback vom Feld-Client (falls verfuegbar) —
  // der writeStore matched den gegen den gesendeten Wert und kann sofort
  // auf `confirmed` oder `mismatched` kippen ohne auf den naechsten Poll
  // zu warten.
  onCommandWriteEvent({
    correlationId: evt.c,
    leafId: evt.leafId,
    status: evt.s,
    error: evt.e,
    readbackValue: evt.v,
  });
}

// Module-scope initialisation guard: bootstrapCommands() can be called
// multiple times (e.g. from tests + from App.tsx); the second call is a no-op.
let unsubHub: (() => void) | null = null;

/**
 * Idempotent init — wires the hub listener and kicks the connection open so
 * the auto-join of `commands-{userId}` actually happens. Call once from
 * App.tsx alongside `bootstrapSession()`. Called from within useEffect the
 * second call short-circuits.
 */
export function bootstrapCommands(): void {
  if (unsubHub) return;
  unsubHub = onCommands(handleServerEvent);
  // The values channel is what usually starts the hub; if the user never opens
  // a plant page we still want to receive command events. `ensureHubStarted`
  // is idempotent (mirrors the alarms bootstrap).
  void ensureHubStarted().catch((err: unknown) => {
    console.error('[commandsStore] hub start failed', err);
  });
}
